import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo } from 'react';
import { queryTestRuns } from '@/features/executions/data/testRuns';
import { useDailyHistoryPanelState } from '@/features/shared/hooks/useDailyHistoryPanelState';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { querySuites } from '@/features/suites/data/suites';
import {
  buildSuiteHistoryQueryInput,
  buildSuiteHistoryQueryStateKey,
} from '@/features/suites/pages/suiteHistoryQueries';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { gridPaths, queryKeys } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildSuiteHistoryRows } from './suiteHistoryRows';
import type { DailyHistorySortKey } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type { SuiteRunStatus, TestRunDto } from '@flowqa/shared';

type SuiteHistoryDateWindow = {
  startDay: string | null;
  endDay: string | null;
  previousStartDay: string | null;
  previousEndDay: string | null;
};

type UseSuiteHistoryPanelDataArgs = {
  panelDateQuickFilter: string;
  panelDateWindow: SuiteHistoryDateWindow;
  panelSearchValue: string;
  panelFilterStatuses: SuiteRunStatus[];
  panelFilterSuites: string[];
  panelFilterTags: string[];
  expandedSuiteIds: string[];
  latestDateBounds: {
    startDay: string | null;
    endDay: string | null;
  };
  enabled?: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

type UseSuiteHistoryPanelDataResult = {
  dailyHistoryRows: ReturnType<typeof buildSuiteHistoryRows>;
  historyPage: number;
  historyPageSize: number;
  historySortDirection: 'asc' | 'desc';
  historySortKey: DailyHistorySortKey;
  historyLatestDateBounds: {
    from: Dayjs | null;
    to: Dayjs | null;
  };
  dailyHistoryLoading: boolean;
  showErrorState: boolean;
  expandedSuiteIdsLoading: Set<string>;
  totalCount: number;
  setHistoryPage: (nextPage: number) => void;
  setHistoryPageSize: (nextPageSize: number) => void;
  setHistorySortDirection: (nextSortDirection: 'asc' | 'desc') => void;
  setHistorySortKey: (nextSortKey: DailyHistorySortKey) => void;
};

export const useSuiteHistoryPanelData = ({
  panelDateQuickFilter,
  panelDateWindow,
  panelSearchValue,
  panelFilterStatuses,
  panelFilterSuites,
  panelFilterTags,
  expandedSuiteIds,
  latestDateBounds,
  enabled = true,
  selectedOrganisationId,
  selectedProjectId,
}: UseSuiteHistoryPanelDataArgs): UseSuiteHistoryPanelDataResult => {
  const { preferFileNameOverDisplayName } = useProjectStatusPresentation();
  const {
    page: historyPage,
    pageSize: historyPageSize,
    sortKey: historySortKey,
    sortDirection: historySortDirection,
    isTransitionLoading,
    setPage: setHistoryPage,
    setPageSize: setHistoryPageSize,
    setSortKey: setHistorySortKey,
    setSortDirection: setHistorySortDirection,
  } = useDailyHistoryPanelState({
    defaultSortKey: 'name',
  });
  const effectiveStartDay =
    panelDateWindow.startDay ??
    (panelDateQuickFilter === 'latest' ? latestDateBounds.startDay : null);
  const effectiveEndDay =
    panelDateWindow.endDay ?? (panelDateQuickFilter === 'latest' ? latestDateBounds.endDay : null);
  const shouldResolveFullLatestBounds =
    panelDateQuickFilter === 'latest' &&
    panelDateWindow.startDay === null &&
    panelDateWindow.endDay === null;
  const panelFilterStatusesKey = useMemo(
    () => [...panelFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterStatuses],
  );
  const panelFilterSuitesKey = useMemo(
    () => [...panelFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterSuites],
  );
  const panelFilterTagsKey = useMemo(
    () => [...panelFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [panelFilterTags],
  );
  const latestBoundsFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('executionStatus', panelFilterStatuses),
        buildInFilter('id', panelFilterSuites),
        buildInFilter('tags', panelFilterTags),
      ),
    [
      panelFilterStatuses,
      panelFilterStatusesKey,
      panelFilterSuites,
      panelFilterSuitesKey,
      panelFilterTags,
      panelFilterTagsKey,
    ],
  );
  const earliestLatestSuiteQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.suites,
      'daily-history-latest-bounds',
      JSON.stringify({
        panelSearchValue,
        panelFilterStatuses,
        panelFilterSuites,
        panelFilterTags,
        selectedOrganisationId,
        selectedProjectId,
        direction: 'asc',
      }),
    ),
    queryFn: async () =>
      await querySuites(selectedProjectId, selectedOrganisationId ?? undefined, {
        page: 1,
        pageSize: 1,
        search: panelSearchValue.trim(),
        sorts: [{ field: 'latestRunTimestamp', direction: 'asc' }],
        filters: latestBoundsFilters,
        include: { latestRun: true },
      }),
    enabled: enabled && shouldResolveFullLatestBounds,
    placeholderData: keepPreviousData,
  });
  const latestLatestSuiteQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.suites,
      'daily-history-latest-bounds',
      JSON.stringify({
        panelSearchValue,
        panelFilterStatuses,
        panelFilterSuites,
        panelFilterTags,
        selectedOrganisationId,
        selectedProjectId,
        direction: 'desc',
      }),
    ),
    queryFn: async () =>
      await querySuites(selectedProjectId, selectedOrganisationId ?? undefined, {
        page: 1,
        pageSize: 1,
        search: panelSearchValue.trim(),
        sorts: [{ field: 'latestRunTimestamp', direction: 'desc' }],
        filters: latestBoundsFilters,
        include: { latestRun: true },
      }),
    enabled: enabled && shouldResolveFullLatestBounds,
    placeholderData: keepPreviousData,
  });

  const dailyHistoryQueryKey = useMemo(
    () =>
      buildSuiteHistoryQueryStateKey({
        panelDateQuickFilter,
        panelDateWindowEndDay: effectiveEndDay,
        panelDateWindowStartDay: effectiveStartDay,
        panelSearchValue,
        panelFilterStatuses,
        panelFilterSuites,
        panelFilterTags,
        historySortDirection,
        historySortKey,
        historyPage,
        historyPageSize,
        selectedOrganisationId,
        selectedProjectId,
      }),
    [
      panelDateQuickFilter,
      effectiveEndDay,
      effectiveStartDay,
      panelSearchValue,
      panelFilterStatusesKey,
      panelFilterSuitesKey,
      panelFilterTagsKey,
      historySortDirection,
      historySortKey,
      historyPage,
      historyPageSize,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );

  const dailyHistoryQuery = useQuery({
    queryKey: queryKeys.dataGrid(gridPaths.suites, 'daily-history', dailyHistoryQueryKey),
    queryFn: async () =>
      await querySuites(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        buildSuiteHistoryQueryInput({
          panelDateWindowEndDay: effectiveEndDay,
          panelDateWindowStartDay: effectiveStartDay,
          panelSearchValue,
          panelFilterStatuses,
          panelFilterSuites,
          panelFilterTags,
          historySortDirection,
          historySortKey,
          historyPage,
          historyPageSize,
        }),
      ),
    enabled,
    placeholderData: keepPreviousData,
  });

  const visibleSuiteIds = useMemo(
    () =>
      (dailyHistoryQuery.data?.items ?? [])
        .map((suite) => suite.id)
        .sort((left, right) => left.localeCompare(right)),
    [dailyHistoryQuery.data?.items],
  );
  const historyLatestDateBounds = useMemo(() => {
    const earliestLatestTimestamp =
      earliestLatestSuiteQuery.data?.items[0]?.latestRun?.timestamp ?? null;
    const latestLatestTimestamp =
      latestLatestSuiteQuery.data?.items[0]?.latestRun?.timestamp ?? null;
    if (earliestLatestTimestamp !== null || latestLatestTimestamp !== null) {
      return {
        from: earliestLatestTimestamp ? dayjs(earliestLatestTimestamp) : null,
        to: latestLatestTimestamp ? dayjs(latestLatestTimestamp) : null,
      };
    }
    const timestamps = (dailyHistoryQuery.data?.items ?? [])
      .map((suite) => suite.latestRun?.timestamp ?? null)
      .filter((timestamp): timestamp is Date => timestamp !== null)
      .map((timestamp) => dayjs(timestamp));
    if (timestamps.length === 0) {
      return {
        from: effectiveStartDay ? dayjs(effectiveStartDay) : null,
        to: effectiveEndDay ? dayjs(effectiveEndDay) : null,
      };
    }
    return timestamps.reduce(
      (bounds, timestamp) => ({
        from: bounds.from === null || timestamp.isBefore(bounds.from) ? timestamp : bounds.from,
        to: bounds.to === null || timestamp.isAfter(bounds.to) ? timestamp : bounds.to,
      }),
      { from: null as Dayjs | null, to: null as Dayjs | null },
    );
  }, [
    dailyHistoryQuery.data?.items,
    earliestLatestSuiteQuery.data?.items,
    effectiveEndDay,
    effectiveStartDay,
    latestLatestSuiteQuery.data?.items,
  ]);

  const visibleExpandedSuiteIds = useMemo(
    () =>
      expandedSuiteIds
        .filter((suiteId) => visibleSuiteIds.includes(suiteId))
        .sort((left, right) => left.localeCompare(right)),
    [expandedSuiteIds, visibleSuiteIds],
  );
  const latestOnlyHistoryMode =
    panelDateQuickFilter === 'latest' &&
    panelDateWindow.startDay === null &&
    panelDateWindow.endDay === null;

  const expandedSuiteRunQueries = useQueries({
    queries: visibleExpandedSuiteIds.map((suiteId) => ({
      queryKey: queryKeys.dataGrid(
        gridPaths.suites,
        'daily-history-test-runs',
        JSON.stringify({
          panelDateWindowEndDay: effectiveEndDay,
          panelDateWindowStartDay: effectiveStartDay,
          panelFilterStatuses,
          panelSearchValue,
          selectedOrganisationId,
          selectedProjectId,
          suiteId,
        }),
      ),
      queryFn: async () =>
        await fetchAllQueryPages(
          async ({ page, pageSize }) =>
            await queryTestRuns(selectedProjectId, selectedOrganisationId ?? undefined, {
              page,
              pageSize,
              search: panelSearchValue,
              sorts: [
                { field: 'timestamp', direction: 'desc' },
                { field: 'id', direction: 'desc' },
              ],
              filters: mergeFilters(
                buildInFilter('suiteId', [suiteId]),
                buildInFilter('status', panelFilterStatuses),
                buildDateRangeFilters(
                  'timestamp',
                  effectiveStartDay ? `${effectiveStartDay}T00:00:00.000Z` : null,
                  effectiveEndDay ? `${effectiveEndDay}T23:59:59.999Z` : null,
                ),
              ),
            }),
        ),
      enabled: enabled && effectiveStartDay !== null && effectiveEndDay !== null,
    })),
  });

  const suiteTestRunsBySuiteId = useMemo(() => {
    const next = new Map<string, TestRunDto[]>();
    visibleExpandedSuiteIds.forEach((suiteId, index) => {
      next.set(suiteId, expandedSuiteRunQueries[index]?.data?.items ?? []);
    });
    return next;
  }, [expandedSuiteRunQueries, visibleExpandedSuiteIds]);

  const expandedSuiteIdsLoading = useMemo(() => {
    const next = new Set<string>();
    visibleExpandedSuiteIds.forEach((suiteId, index) => {
      if (expandedSuiteRunQueries[index]?.isFetching) {
        next.add(suiteId);
      }
    });
    return next;
  }, [expandedSuiteRunQueries, visibleExpandedSuiteIds]);

  const dailyHistoryRows = useMemo(
    () =>
      buildSuiteHistoryRows(
        dailyHistoryQuery.data?.items,
        suiteTestRunsBySuiteId,
        new Set(visibleExpandedSuiteIds),
        expandedSuiteIdsLoading,
        panelFilterStatuses,
        latestOnlyHistoryMode,
        { preferFileNameOverDisplayName },
      ),
    [
      dailyHistoryQuery.data?.items,
      expandedSuiteIdsLoading,
      panelFilterStatuses,
      latestOnlyHistoryMode,
      preferFileNameOverDisplayName,
      suiteTestRunsBySuiteId,
      visibleExpandedSuiteIds,
    ],
  );

  useEffect(() => {
    if (historyPage === 0) {
      return;
    }
    setHistoryPage(0);
  }, [
    panelDateQuickFilter,
    effectiveEndDay,
    effectiveStartDay,
    historyPage,
    panelFilterStatusesKey,
    panelFilterSuitesKey,
    panelFilterTagsKey,
    panelSearchValue,
    selectedOrganisationId,
    selectedProjectId,
    setHistoryPage,
  ]);

  return {
    dailyHistoryRows,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    historyLatestDateBounds,
    dailyHistoryLoading:
      isTransitionLoading ||
      (dailyHistoryQuery.data === undefined && dailyHistoryQuery.isPending) ||
      (earliestLatestSuiteQuery.data === undefined && earliestLatestSuiteQuery.isFetching) ||
      (latestLatestSuiteQuery.data === undefined && latestLatestSuiteQuery.isFetching),
    showErrorState:
      dailyHistoryQuery.isError ||
      earliestLatestSuiteQuery.isError ||
      latestLatestSuiteQuery.isError ||
      expandedSuiteRunQueries.some((query) => query.isError),
    expandedSuiteIdsLoading,
    totalCount: dailyHistoryQuery.data?.total ?? 0,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
  };
};
